import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { projectsAPI, trainingAPI, usersAPI } from '../utils/api';
import { ConfirmDialog, Modal, FormField } from '../components/layout/UI';
import { format, parseISO, isValid } from 'date-fns';
import { useToast, apiError } from '../components/Toast';
import CustomFieldsEditor from '../components/CustomFieldsEditor';
import { ArrowLeft, Calendar, Clock, Paperclip, SquarePen, Star, Trash2, User } from 'lucide-react';

const EditIc = () => <SquarePen size={14} />;
const TrashIc = () => <Trash2 size={14} />;
const ClipIc = () => <Paperclip size={14} />;
const StarIc = ({ filled }) => (
  <Star size={18} strokeWidth={1.5} fill={filled ? 'currentColor' : 'none'} />
);
const ClockIc = () => <Clock size={14} />;
const CalIc = () => <Calendar size={14} />;

const fmt = (val, pattern = 'MMMM d, yyyy') => {
  if (!val) return null;
  try { const d = typeof val === 'string' ? parseISO(val) : new Date(val); return isValid(d) ? format(d, pattern) : null; }
  catch { return null; }
};

const InfoRow = ({ label, value, icon, children }) => (
  value || children ? (
    <div className="flex gap-3 py-2.5 border-b" style={{ borderColor: 'var(--border)' }}>
      <p className="text-xs font-semibold w-32 flex-shrink-0 pt-0.5 uppercase tracking-wider flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
        {icon}{label}
      </p>
      <div className="flex-1 text-sm" style={{ color: 'var(--text)' }}>{children || value}</div>
    </div>
  ) : null
);

const ScoreBar = ({ score }) => {
  const pct   = score ? (score / 10) * 100 : 0;
  const color = pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
  const stars = Math.round(score / 2);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex gap-0.5" style={{ color: '#f59e0b' }}>
          {[1,2,3,4,5].map(i => <StarIc key={i} filled={i <= stars} />)}
        </div>
        <span className="text-2xl font-bold" style={{ color }}>{score}<span className="text-base font-normal">/10</span></span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

export default function TrainingDetailPage() {
  const toast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [session,    setSession]    = useState(null);
  const [error, setError] = useState('');
  const [projects,   setProjects]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen,   setEditOpen]   = useState(false);
  const [editForm,   setEditForm]   = useState({});
  const [saving,     setSaving]     = useState(false);
  // The Conducted by field needs the list; this page never loaded one,
  // because the name was only ever displayed, never chosen.
  const [users, setUsers] = useState([]);
  useEffect(() => {
    usersAPI.getAll()
      .then(r => setUsers(r.data || []))
      .catch(() => setUsers([]));
  }, []);

  const [activeTab,  setActiveTab]  = useState('overview');

  const f = v => setEditForm(prev => ({ ...prev, ...v }));

  useEffect(() => {
    Promise.all([trainingAPI.getById(id), projectsAPI.getAll({ limit: 0 })])
      .then(([sR, pR]) => { setSession(sR.data); setProjects(pR.data); })
      .catch(e => { toast.error(apiError(e, 'Training load error:')); })
      .finally(() => setLoading(false));
  }, [id]);

  const openEdit = () => {
    const participants = Array.isArray(session.participants) ? session.participants.join(', ') : (session.participants || '');
    setEditForm({
      project_id:      session.project_id,
      session_name:    session.session_name,
      session_date:    session.session_date ? String(session.session_date).substring(0, 10) : '',
      participants,
      topics_covered:  session.topics_covered || '',
      duration_minutes:session.duration_minutes || '',
      feedback_score:  session.feedback_score || '',
      notes:           session.notes || '',
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await trainingAPI.update(id, editForm);
      toast.success('Training session updated');
      const r = await trainingAPI.getById(id);
      setSession(r.data);
      setEditOpen(false);
    } catch(e) { toast.error(apiError(e, 'Failed to save training session')); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await trainingAPI.delete(id); toast.success('Training session deleted'); navigate('/training'); }
    catch(e) { toast.error(apiError(e, 'Could not delete that session.')); }
  };


  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!session) return <div className="card text-center py-16"><p style={{ color: 'var(--text)' }}>Training session not found</p><button onClick={() => navigate('/training')} className="btn-secondary mt-3">← Back</button></div>;

  const participants = Array.isArray(session.participants) ? session.participants : [];

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm flex-wrap" style={{ color: 'var(--text-secondary)' }}>
        <button onClick={() => navigate('/training')} className="hover:text-brand-500 flex items-center gap-1"><ArrowLeft size={16}/> Training</button>
        {session.project_name && <><span>/</span><span>{session.project_name}</span></>}
        <span>/</span><span style={{ color: 'var(--text)' }}>{session.session_name}</span>
      </div>

      {/* Header card */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text)' }}>{session.session_name}</h1>
            <div className="flex flex-wrap gap-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
              {session.session_date && <span className="flex items-center gap-1.5"><CalIc />{fmt(session.session_date)}</span>}
              {session.duration_minutes && <span className="flex items-center gap-1.5"><ClockIc />{session.duration_minutes} min ({(session.duration_minutes/60).toFixed(1)}h)</span>}
              {participants.length > 0 && <span className="flex items-center gap-1.5"><User size={14}/>{participants.length} participant{participants.length !== 1 ? 's' : ''}</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={openEdit} className="btn-secondary flex items-center gap-1.5 text-sm"><EditIc /> Edit</button>
            <button onClick={() => setDeleteOpen(true)} className="btn-danger flex items-center gap-1.5 text-sm"><TrashIc /> Delete</button>
          </div>
        </div>
        {session.feedback_score != null && (
          <div className="mt-2 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)' }}>Feedback Score</p>
            <ScoreBar score={session.feedback_score} />
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="border-b flex gap-1 overflow-x-auto" style={{ borderColor:'var(--border)' }}>
        {[
          { key:'overview',     label:'Overview'                               },
          { key:'participants', label:'Participants', count:participants.length },
          { key:'feedback',     label:'Feedback'                               },
          { key:'attachments',  label:'Materials',   count:(session.attachments||[]).length },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors
              ${activeTab === tab.key ? 'border-brand-500 text-brand-500' : 'border-transparent'}`}
            style={activeTab !== tab.key ? { color:'var(--text-secondary)' } : {}}>
            {tab.label}
            {tab.count > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full"
                style={{ background:activeTab===tab.key?'#2E539F':'var(--surface-alt)', color:activeTab===tab.key?'white':'var(--text-secondary)' }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left */}
        <div className="space-y-5">
          <div className="card">
            <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--text)' }}>Session Info</h3>
            <InfoRow label="Project">{session.project_name ? <Link to={`/projects/${session.project_id}`} className="text-brand-500 hover:underline">{session.project_name}</Link> : '—'}</InfoRow>
            <InfoRow label="Conducted By" value={session.conducted_by_name || '—'} />
            <InfoRow label="Date">{session.session_date ? fmt(session.session_date) : '—'}</InfoRow>
            <InfoRow label="Duration">{session.duration_minutes ? `${session.duration_minutes} min` : '—'}</InfoRow>
            <InfoRow label="Recorded">{fmt(session.created_at, 'MMM d, yyyy HH:mm')}</InfoRow>
          </div>

          {participants.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: 'var(--text)' }}>
                <User size={14}/> Participants ({participants.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {participants.map((p, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg" style={{ background: 'var(--surface-alt)', color: 'var(--text)' }}>
                    <div className="w-5 h-5 rounded flex items-center justify-center font-bold text-white text-xs" style={{ background: `hsl(${(i*47)%360},65%,55%)` }}>
                      {p.trim()[0]?.toUpperCase()}
                    </div>
                    {p.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}

          {session.attachments?.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: 'var(--text)' }}><ClipIc /> Attachments ({session.attachments.length})</h3>
              <div className="space-y-2">
                {session.attachments.map((att, i) => (
                  <a key={i} href={`/uploads/${att.filename}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2.5 rounded-xl hover:bg-[var(--surface-alt)] transition-colors text-brand-500 text-sm">
                    <ClipIc /> <span className="truncate">{att.original_name}</span>
                    {att.file_size && <span className="ml-auto text-xs flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>{(att.file_size/1024).toFixed(1)} KB</span>}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right */}
        <div className="lg:col-span-2 space-y-5">
          {session.topics_covered && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--text)' }}>Topics Covered</h3>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--text)' }}>{session.topics_covered}</p>
            </div>
          )}
          {session.notes && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--text)' }}>Notes</h3>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--text)' }}>{session.notes}</p>
            </div>
          )}
          {!session.topics_covered && !session.notes && (
            <div className="card text-center py-12">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No additional notes or topics recorded for this session.</p>
            </div>
          )}
        </div>
      </div>}

      {/* Participants Tab */}
      {activeTab === 'participants' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm" style={{ color:'var(--text)' }}>Participants ({participants.length})</h3>
            <span className="text-xs px-2.5 py-1 rounded-full" style={{ background:'rgba(46,83,159,.1)', color:'#2E539F' }}>
              {session.duration_minutes ? `${Math.floor(session.duration_minutes/60)}h ${session.duration_minutes%60}m session` : ''}
            </span>
          </div>
          {participants.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-3xl mb-2">👥</p>
              <p className="text-sm" style={{ color:'var(--text-secondary)' }}>No participants recorded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {participants.map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ background:'var(--surface-alt)' }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-sm flex-shrink-0"
                    style={{ background:'#2E539F' }}>
                    {String(p).trim()[0]?.toUpperCase() || '?'}
                  </div>
                  <p className="text-sm font-medium" style={{ color:'var(--text)' }}>{String(p).trim()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Feedback Tab */}
      {activeTab === 'feedback' && (
        <div className="space-y-4">
          <div className="card">
            <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Satisfaction Score</h3>
            {session.feedback_score != null ? (
              <div className="flex flex-col items-center py-6">
                <p className="text-6xl font-black mb-2"
                  style={{ color:session.feedback_score>=8?'#10b981':session.feedback_score>=6?'#FFC849':'#ef4444' }}>
                  {session.feedback_score}<span className="text-2xl text-gray-400">/10</span>
                </p>
                <div className="w-full max-w-xs h-3 rounded-full overflow-hidden mt-4" style={{ background:'var(--border)' }}>
                  <div className="h-full rounded-full transition-all"
                    style={{ width:`${(session.feedback_score/10)*100}%`, background:session.feedback_score>=8?'#10b981':session.feedback_score>=6?'#FFC849':'#ef4444' }}/>
                </div>
                <p className="text-sm mt-3" style={{ color:'var(--text-secondary)' }}>
                  {session.feedback_score >= 9 ? 'Excellent' : session.feedback_score >= 7 ? 'Good' : session.feedback_score >= 5 ? 'Average' : 'Needs Improvement'}
                </p>
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-3xl mb-2">📊</p>
                <p className="text-sm" style={{ color:'var(--text-secondary)' }}>No feedback score recorded yet</p>
              </div>
            )}
          </div>
          {session.notes && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Notes & Observations</h3>
              <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color:'var(--text)' }}>{session.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Materials / Attachments Tab */}
      {activeTab === 'attachments' && (
        <div className="card">
          <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Training Materials</h3>
          {(session.attachments||[]).length === 0 ? (
            <div className="text-center py-12">
              <p className="text-3xl mb-2">📎</p>
              <p className="text-sm" style={{ color:'var(--text-secondary)' }}>No materials uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {(session.attachments||[]).map(a => (
                <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border" style={{ borderColor:'var(--border)' }}>
                  <span className="text-xl flex-shrink-0">📄</span>
                  <div className="flex-1 min-w-0">
                    <a href={a.url} target="_blank" rel="noopener noreferrer"
                      className="text-sm font-medium text-brand-500 hover:underline truncate block">{a.filename}</a>
                    {a.size && <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{(a.size/1024).toFixed(0)} KB</p>}
                  </div>
                  <a href={a.url} target="_blank" rel="noopener noreferrer"
                    className="text-xs px-3 py-1.5 rounded-lg border hover:bg-[var(--surface-alt)] transition-colors flex-shrink-0"
                    style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
                    Download
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title="Edit Training Session" size="lg">
        <div className="space-y-4">
          <FormField label="Project" required>
            <select className="select" value={editForm.project_id||''} onChange={e=>f({project_id:e.target.value})}>
              {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FormField>
          <FormField label="Session Name" required>
            <input className="input" value={editForm.session_name||''} onChange={e=>f({session_name:e.target.value})} />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Date" required>
              <input type="date" className="input" value={editForm.session_date||''} onChange={e=>f({session_date:e.target.value})} />
            </FormField>
            <FormField label="Duration (minutes)">
              <input type="number" className="input" value={editForm.duration_minutes||''} onChange={e=>f({duration_minutes:e.target.value})} placeholder="60" min="0" />
            </FormField>
          </div>
          <FormField label="Participants (comma-separated)">
            <input className="input" value={editForm.participants||''} onChange={e=>f({participants:e.target.value})} placeholder="John Doe, Jane Smith" />
          </FormField>
          <FormField label="Feedback Score (0–10)">
            <input type="number" className="input" value={editForm.feedback_score||''} onChange={e=>f({feedback_score:e.target.value})} placeholder="8.5" min="0" max="10" step="0.1" />
          </FormField>
          <FormField label="Topics Covered">
            <textarea className="input" rows={3} value={editForm.topics_covered||''} onChange={e=>f({topics_covered:e.target.value})} />
          </FormField>
          <FormField label="Notes">
            <textarea className="input" rows={2} value={editForm.notes||''} onChange={e=>f({notes:e.target.value})} />
          </FormField>
          <FormField label="Conducted by">
            <select className="select" value={editForm.conducted_by||''} onChange={e=>f({conducted_by:e.target.value})}>
              <option value="">Not recorded</option>
              {users.filter(u=>u.is_active).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
              Accepted by the API all along; this page had no field for it, so a
              session recorded by one person could not be reassigned to another.
            </p>
          </FormField>

          <CustomFieldsEditor appliesTo="training"
            values={editForm.custom_fields || {}}
            onChange={v => f({ custom_fields: v })} />
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setEditOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving||!editForm.session_name} className="btn-primary flex items-center gap-2">
              {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
              Save Changes
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={handleDelete}
        title="Delete Training Session" message={`Delete "${session.session_name}" permanently?`} />
    </div>
  );
}
